import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Grid,
  Button,
  Typography,
  styled,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import { Search, X, ChevronDown, ChevronUp, Filter } from "lucide-react";
import axios from "axios";
import useUserId from "hooks/useUserId";
import { PostSearchBar } from "components/searchBars/SearchBars";
import { useTranslation } from "react-i18next";
import filterIcon from "../../assets/img/filter_icon.svg";
import searchIcon from "../../assets/img/search_icon_grey.svg";
import share_icon from "../../assets/img/share_icon.svg";
import QRCodeDialog from "views/Posts/QRCodeDialog";
import colors from "colors/color";

const apiUrl = process.env.REACT_APP_API_URL;

const StyledTypography = styled(Typography)(() => ({
  color: "#000",
  fontFamily: "Poppins",
  fontWeight: 500,
  fontSize: "14px",
}));

const DynamicFormSearchBar = ({
  data,
  onSearch,
  SEARCH_API_ENDPOINT = `${apiUrl}candidates/api/search_dynamic_form_submissions/`, 
  AUTOCOMPLETE_API_ENDPOINT = `${apiUrl}candidates/api/dynamicform_autocomplete_suggestions/`,
  showShareButton = false,
  adminCompany = "",
  resultCount = 0,
  onNavigateToApplicationTable = null, // Navigation callback to ApplicationTable
  onNavigateToBlacklist = null, // Navigation callback to Blacklist
  enableInfiniteScroll = false, // Add infinite scroll prop
}) => {
  // Define the specific fields we want to use
  const { t } = useTranslation();
  const specificFields = [
    {
      name: "first_name",
      label: t("dynamicFormSearchBar.firstName"),
      formField: "firstName",
    },
    {
      name: "last_name",
      label: t("dynamicFormSearchBar.lastName"),
      formField: "lastName",
    },
    { name: "city", label: "Localisation", formField: "city" },
    {
      name: "experience_years",
      label: t("dynamicFormSearchBar.experienceYears"),
      formField: "experienceYears",
    },
    {
      name: "education_level",
      label: t("dynamicFormSearchBar.educationLevel"),
      formField: "educationLevel",
    },
    {
      name: "category_profile",
      label: "Catégorie",
      formField: "CategoryProfile",
    },
    {
      name: "subcategory_profile",
      label: "Sous-catégorie",
      formField: "SubCategoryProfile",
    },
  ];

  const [fields, setFields] = useState(specificFields);
  // Get user ID from hook
  const userId = useUserId();

  // Sharing functionality state
  const [qrCodeUrl, setQRCodeUrl] = useState("");
  const [showQRCodeDialog, setShowQRCodeDialog] = useState(false);

  // State for search inputs - dynamically populated based on fields
  const [searchParams, setSearchParams] = useState({
    recruiter_id: userId?.userId || "", // Initialize with userId if available immediately
  });

  // State for showing/hiding the advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Set recruiter_id from userId on component mount and when userId changes
  useEffect(() => {
    if (userId && userId.userId) {
      console.log("Setting recruiter_id:", userId.userId);
      setSearchParams((prev) => ({
        ...prev,
        recruiter_id: userId.userId,
      }));
    }
  }, [userId]);

  // State for autocomplete
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [currentAutocompleteField, setCurrentAutocompleteField] = useState("");

  // State for active filters
  const [activeFilters, setActiveFilters] = useState([]);

  // Remove category names mapping state since it's now handled by the backend

  // State for source filter
  const [sourceFilterAnchor, setSourceFilterAnchor] = useState(null);
  const [sourceFilters, setSourceFilters] = useState({
    regular: true,      // Candidat
    scraped: true,      // Candidat Importé – Lié
    scraped_users: true // Candidat Importé – Non Lié
  });

  // State for additional navigation filters
  const [additionalFilters, setAdditionalFilters] = useState({
    blacklist: false,
    applicationTable: false
  });

  // Refs for each input field
  const mainSearchInputRef = useRef(null);
  const inputRefs = useRef({});
  const searchTimeoutRef = useRef(null);
  const autocompleteContainerRef = useRef(null);

  // Check if we're on the candidature_spontanee_list route to hide filter buttons
  const isSpontaneousListRoute = window.location.pathname.includes('/candidature_spontanee_list');

  // Initialize the search params with all fields
  useEffect(() => {
    const newSearchParams = { ...searchParams };
    fields.forEach((field) => {
      if (!newSearchParams[field.name]) {
        newSearchParams[field.name] = "";
      }
    });
    setSearchParams(newSearchParams);
  }, [fields]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedInsideAutocomplete = false;

      if (
        mainSearchInputRef.current &&
        mainSearchInputRef.current.contains(event.target)
      ) {
        clickedInsideAutocomplete = true;
      }

      if (
        autocompleteContainerRef.current &&
        autocompleteContainerRef.current.contains(event.target)
      ) {
        clickedInsideAutocomplete = true;
      }

      Object.values(inputRefs.current).forEach((ref) => {
        if (ref && ref.contains(event.target)) {
          clickedInsideAutocomplete = true;
        }
      });

      if (!clickedInsideAutocomplete) {
        setShowAutocomplete(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Helper function to clean suggestion text
  const cleanSuggestionText = (text) => {
    if (!text) return "";

    // Remove brackets and quotes from the text
    return text
      .replace(/^\[+|\]+$/g, "") // Remove brackets at the beginning and end
      .replace(/^"|"$/g, "") // Remove quotes at the beginning and end
      .replace(/^'|'$/g, "") // Remove single quotes at the beginning and end
      .trim();
  };

  // Remove the separate category names fetching since it's now included in the main response

  // Remove fetchCategoryNames function since category names are now handled by the backend

  // Fetch autocomplete suggestions
  const fetchAutocompleteSuggestions = async (query, field) => {
    if (!query || query.length < 2) return;

    // Map the field name to the expected 'type' parameter
    let type;

    switch (field) {
      case "first_name":
      case "last_name":
        type = "name";
        break;
      case "city":
        type = "location";
        break;
      case "phone":
        type = "phone";
        break;
      case "experience_years":
        type = "experience";
        break;
      case "education_level":
        type = "education";
        break;
      case "category_profile":
        type = "category";
        break;
      case "subcategory_profile":
        type = "subcategory";
        break;
      default:
        type = field;
    }

    try {
      const response = await axios.get(AUTOCOMPLETE_API_ENDPOINT, {
        params: {
          query,
          type, // Use the mapped type value
          recruiter_id: searchParams.recruiter_id || userId?.userId || "",
        },
      });

      // Log the request URL
      console.log("Request URL:", response.config.url);

      // Clean up the suggestions
      const cleanedSuggestions = (response.data.suggestions || []).map(
        (suggestion) => cleanSuggestionText(suggestion)
      );

      // For category/subcategory fields, the backend now returns names directly
      setAutocompleteResults(cleanedSuggestions);

      setShowAutocomplete(cleanedSuggestions.length > 0);
    } catch (error) {
      console.error("Error fetching autocomplete suggestions:", error);
      setAutocompleteResults([]);
      setShowAutocomplete(false);
    }
  };

  // Handle input changes for search fields
  const handleInputChange = (field, value) => {
    setSearchParams((prev) => ({ ...prev, [field]: value }));

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    setCurrentAutocompleteField(field);

    searchTimeoutRef.current = setTimeout(() => {
      if (value.length >= 1) {
        fetchAutocompleteSuggestions(value, field);
      } else {
        setShowAutocomplete(false);
      }
    }, 300);
  };

  const handleAutocompleteSelect = (value) => {
    // Update the search parameter
    const updatedParams = {
      ...searchParams,
      [currentAutocompleteField]: value,
    };
    setSearchParams(updatedParams);
    setShowAutocomplete(false);

    // Add to active filters
    if (
      !activeFilters.some(
        (filter) =>
          filter.field === currentAutocompleteField && filter.value === value
      )
    ) {
      setActiveFilters([
        ...activeFilters,
        { field: currentAutocompleteField, value },
      ]);
    }

    // Immediately trigger a search with the updated params
    performSearch(updatedParams);
  };

  // Perform search API call
  const performSearch = (params) => {
    // Ensure recruiter_id is included if available but not in params
    const searchParams = {
      ...params,
      recruiter_id: params.recruiter_id || userId?.userId || "",
    };


    axios
      .get(SEARCH_API_ENDPOINT, { params: searchParams })
      .then((response) => {

        // Handle different response formats and extract total count
        let results = [];
        let totalCount = null;

        if (response.data.submissions) {
          results = response.data.submissions.map((item) => item.submission);
          totalCount = response.data.count || results.length;
        } else if (Array.isArray(response.data)) {
          results = response.data;
          totalCount = results.length;
        } else {
          results = [];
          totalCount = 0;
        }

        // Pass both results, total count, and search parameters to onSearch
        onSearch(results, totalCount, searchParams);
      })
      .catch((error) => {
        console.error("Error searching dynamic forms:", error);
        onSearch([], 0, searchParams);
      });
  };

  // Handle form submission
  const handleSearch = () => {
    // Ensure recruiter_id is included
    const paramsWithRecruiterId = {
      ...searchParams,
      recruiter_id: searchParams.recruiter_id || userId?.userId || "",
    };

    // Filter out empty values for display, but keep recruiter_id
    const filtersForDisplay = Object.entries(paramsWithRecruiterId)
      .filter(([key, value]) => value !== "" && key !== "recruiter_id")
      .map(([field, value]) => ({ field, value }));

    // Update active filters (exclude recruiter_id)
    setActiveFilters(filtersForDisplay);

    // If no filters except recruiter_id, reset to full data
    if (filtersForDisplay.length === 0 && !paramsWithRecruiterId.recruiter_id) {
      // Handle both array data and paginated response
      if (Array.isArray(data)) {
        onSearch(data, data.length, paramsWithRecruiterId);
      } else if (data && data.results) {
        onSearch(data.results, data.count || data.results.length, paramsWithRecruiterId);
      } else {
        onSearch([], 0, paramsWithRecruiterId);
      }
      return;
    }

    // Make the API call to search
    performSearch(paramsWithRecruiterId);
  };

  const handleRemoveFilter = (filterToRemove) => {
    // Update active filters first
    setActiveFilters(
      activeFilters.filter(
        (filter) =>
          !(
            filter.field === filterToRemove.field &&
            filter.value === filterToRemove.value
          )
      )
    );

    // Update search params
    const updatedParams = { ...searchParams };
    updatedParams[filterToRemove.field] = "";
    setSearchParams(updatedParams);

    // Make API call with updated params
    performSearch(updatedParams);
  };

  // Handle clearing all filters
  const handleClearAllFilters = () => {
    // Preserve the recruiter_id when clearing filters
    const recruiter_id = searchParams.recruiter_id || userId?.userId || "";

    // Reset all search params
    const clearedParams = { recruiter_id };
    fields.forEach((field) => {
      clearedParams[field.name] = "";
    });

    setSearchParams(clearedParams);
    setActiveFilters([]);

    // Clear search results and return to original data
    onSearch(null, 0, clearedParams); // This will trigger fetchApplications in DynamicFormTable
  };

  // Render autocomplete dropdown for a specific field
  const renderAutocomplete = (field) => {
    if (showAutocomplete && currentAutocompleteField === field) {
      return (
        <Paper
          ref={autocompleteContainerRef}
          elevation={3}
          sx={{
            position: "absolute",
            width: "100%",
            maxHeight: "200px",
            overflow: "auto",
            zIndex: 1000,
            marginTop: "4px",
          }}
        >
          <List dense>
            {autocompleteResults.length > 0 ? (
              autocompleteResults.map((result, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    onClick={() => handleAutocompleteSelect(result)}
                  >
                    <ListItemText primary={result} />
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText
                  primary={t("dynamicFormSearchBar.noResultsFound")}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      );
    }
    return null;
  };

  // Get primary field for main search
  const getPrimaryField = () => {
    // For this specific implementation, we'll use first_name as the primary field
    return fields.find((f) => f.name === "first_name") || fields[0];
  };

  const primaryField = getPrimaryField();

  // Sharing functionality
  const generateLinkedInPost = (companyName) => {
    console.log("Generating LinkedIn post for company:", companyName);
    return {
      id: `candidature-spontanee/${companyName}`,
      image:
        "https://t4.ftcdn.net/jpg/02/07/64/77/360_F_207647709_iapnIP9OVyWufZCc4f89fQVQJ5c2LLKu.jpg",
      title: `Exciting Career Opportunity at ${companyName}! 🚀`,
      description: `We're growing our team at ${companyName}!

  Are you looking for your next career move in a dynamic and innovative environment? ${companyName} is on the lookout for exceptional talent to join our mission of [company mission].

  🔍 Why join ${companyName}?
  - Collaborative and inclusive work culture
  - Continuous learning and professional growth opportunities
  - Competitive compensation and benefits package
  - Work on challenging projects that make a real impact

  We believe in nurturing talent and helping our team members reach their full potential. If you're passionate, driven, and ready to make your mark, we want to hear from you!`,
      requirements: [
        "Strong problem-solving skills and attention to detail",
        "Excellent communication and teamwork abilities",
        "Passion for innovation and continuous improvement",
        "Adaptability and eagerness to learn",
      ],
      deadline: "no deadline",
      localisation: "Remote", // This needs to be non-null
      mode: "On-site, Hybrid, Remote",
      salary: "Competitive",
      offerType: "Open Positions",
    };
  };

  const copyViewJobUrl = () => {
    const spontaneousUrl = `/candidature-spontanee/${adminCompany}`;
    const url = window.location.origin + spontaneousUrl;

    // Copy to clipboard
    navigator.clipboard.writeText(url).then(() => {
      // Set the URL for QR code and open dialog
      setQRCodeUrl(url);
      setShowQRCodeDialog(true);
    });
  };

  const handleCloseQRCodeDialog = () => {
    setShowQRCodeDialog(false);
  };

  // Handle source filter changes
  const handleSourceFilterChange = (sourceType) => {
    setSourceFilters(prev => {
      const newFilters = {
        ...prev,
        [sourceType]: !prev[sourceType]
      };
      
      // If we're checking a source filter, uncheck all additional filters
      if (newFilters[sourceType]) {
        setAdditionalFilters({
          blacklist: false,
          applicationTable: false
        });
      }
      
      return newFilters;
    });
  };

  // Handle additional filter change
  const handleAdditionalFilterChange = (filterType) => {
    setAdditionalFilters(prev => {
      const newFilters = {
        ...prev,
        [filterType]: !prev[filterType]
      };
      
      // If we're checking an additional filter, uncheck all source filters
      if (newFilters[filterType]) {
        setSourceFilters({
          regular: false,
          scraped: false,
          scraped_users: false
        });
        
        // Also uncheck the other additional filter (mutual exclusivity within additional filters)
        if (filterType === "blacklist") {
          newFilters.applicationTable = false;
        } else if (filterType === "applicationTable") {
          newFilters.blacklist = false;
        }
      }
      
      return newFilters;
    });
    
    // Navigate to appropriate component based on selection
    if (filterType === "blacklist" && onNavigateToBlacklist) {
      onNavigateToBlacklist();
    } else if (filterType === "applicationTable" && onNavigateToApplicationTable) {
      onNavigateToApplicationTable();
    }
  };

  // Handle source filter menu open/close
  const handleSourceFilterMenuOpen = (event) => {
    setSourceFilterAnchor(event.currentTarget);
  };

  const handleSourceFilterMenuClose = () => {
    setSourceFilterAnchor(null);
  };

  // Get display name for source types
  const getSourceDisplayName = (sourceType) => {
    const sourceMap = {
      regular: t("applicationSearchBar.regularCandidate", "Candidat"),
      scraped: t("applicationSearchBar.scrapedCandidate", "Candidat Importé – Lié"),
      scraped_users: t("applicationSearchBar.scrapedUser", "Candidat Importé – Non Lié"),
    };
    return sourceMap[sourceType] || sourceType;
  };

  // Render education level field with autocomplete support
  const renderEducationLevelField = () => {
    const educationField = fields.find((f) => f.name === "education_level");
    if (!educationField) return null;

    return (
      <Grid item xs={12} sm={6} md={4} key="education_level">
        <Box
          sx={{ position: "relative" }}
          ref={(el) => (inputRefs.current.education_level = el)}
        >
          <StyledTypography>
            {t("dynamicFormSearchBar.educationLevel")}
          </StyledTypography>
          <TextField
            fullWidth
            value={searchParams.education_level || ""}
            onChange={(e) =>
              handleInputChange("education_level", e.target.value)
            }
            onFocus={() => setCurrentAutocompleteField("education_level")}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                height: 50,
                backgroundColor: "#fff",
                "& fieldset": {
                  borderColor: "#D9D9D9",
                },
                "&:hover fieldset": {
                  borderColor: "#B0B0B0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976d2",
                },
              },
            }}
            variant="outlined"
            InputProps={{
              endAdornment: searchParams.education_level && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      handleInputChange("education_level", "");
                      setShowAutocomplete(false);
                    }}
                  >
                    <X size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {renderAutocomplete("education_level")}
        </Box>
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container>
        <Grid item md={6} display={{ xs: "none", md: "block" }}>
          {/* Result count on the left */}
          {resultCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                pl: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#0017E7",
                  fontWeight: 700,
                  fontSize: "1.25rem",
                  fontFamily: "Poppins",
                }}
              >
                {resultCount}{" "}
                {t(
                  "dynamicFormSearchBar.applicationsFound",
                  "candidatures trouvées"
                )}
              </Typography>
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {/* Search Box Container */}
          <Box
            sx={{
              position: "relative", // Important for proper autocomplete positioning
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "flex-end" },
                backgroundColor: "#fff",
                width: { xs: "100%", md: "100%" },
                maxWidth: { xs: "100%", md: 716 },
                height: { xs: "auto", md: 70 },
                borderRadius: "20px",
                border: "1px solid #e0e0e0",
                padding: { xs: "8px", md: "10px" },
                gap: { xs: "8px", md: "10px" },
                alignItems: "center",
                marginTop: 2,
                flexDirection: { xs: "column", sm: "row" },
                mb: 2,
              }}
            >
              {/* Search input */}
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  flex: 1,
                }}
                ref={mainSearchInputRef}
              >
                <PostSearchBar
                  variant="outlined"
                  size="small"
                  type="text"
                  placeholder={t("dynamicFormSearchBar.filterByName")}
                  value={searchParams[primaryField?.name] || ""}
                  onChange={(e) =>
                    handleInputChange(primaryField?.name, e.target.value)
                  }
                  onFocus={() =>
                    setCurrentAutocompleteField(primaryField?.name)
                  }
                  sx={{
                    flex: 1,
                    mr: { xs: 0, sm: 1 },
                    mb: { xs: 1, sm: 0 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <img
                          src={searchIcon}
                          height={15}
                          width={15}
                          alt={t("dynamicFormSearchBar.search")}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
                {/* Autocomplete dropdown positioned directly inside the search input box */}
                {renderAutocomplete(primaryField?.name)}
              </Box>

              {/* Search and Filter Buttons */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "row" },
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "space-between", sm: "flex-start" },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  sx={{
                    textTransform: "none",
                    fontFamily: "Poppins",
                    width: { xs: "calc(100% - 60px)", sm: 106 },
                    height: { xs: 45, md: 50 },
                    borderRadius: "11px",
                    p: { xs: "10px", md: "14px 26px" },
                    gap: 10,
                    fontSize: { xs: "0.875rem", md: "1rem" },
                    backgroundColor: "#0017E7",
                    "&:hover": {
                      backgroundColor: "rgba(0, 23, 231, 0.9)",
                    },
                  }}
                >
                  {t("dynamicFormSearchBar.search")}
                </Button>
                
                {/* Hide the source filter button on spontaneous list route */}
                {!isSpontaneousListRoute && (
                  <Button
                    variant="outlined"
                    onClick={handleSourceFilterMenuOpen}
                    sx={{
                      border: "1px solid #0017E7",
                      boxShadow: "none",
                      ":hover": {
                        backgroundColor: "#f0f4ff",
                        boxShadow: "0 2px 4px rgba(0, 23, 231, 0.2)",
                      },
                      transition: "all 0.3s ease",
                      borderRadius: "11px",
                      backgroundColor: "#fff",
                      fontFamily: "Poppins",
                      minWidth: "auto",
                      padding: "8px",
                      height: { xs: 45, md: 50 },
                      width: { xs: 50, md: 50 },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: { xs: 1, sm: 1 },
                      "& .MuiSvgIcon-root": {
                        width: { xs: "18px", md: "20px" },
                        height: { xs: "18px", md: "20px" },
                        color: "#0017E7",
                      },
                    }}
                  >
                    <Filter />
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  sx={{
                    border: "1px solid #0017E7",
                    boxShadow: "none",
                    ":hover": {
                      backgroundColor: "#f0f4ff",
                      boxShadow: "0 2px 4px rgba(0, 23, 231, 0.2)",
                    },
                    transition: "all 0.3s ease",
                    borderRadius: "11px",
                    backgroundColor: "#fff",
                    fontFamily: "Poppins",
                    minWidth: "auto",
                    padding: "8px",
                    height: { xs: 45, md: 50 },
                    width: { xs: 50, md: 50 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginLeft: { xs: 1, sm: 1 },
                    "& img": {
                      width: { xs: "18px", md: "20px" },
                      height: { xs: "18px", md: "20px" },
                    },
                  }}
                >
                  <img
                    src={filterIcon}
                    alt={t("dynamicFormSearchBar.filterIcon")}
                  />
                </Button>
                {showShareButton && (
                  <Button
                    sx={{
                      border: `1px solid ${colors.primary}`,
                      width: { xs: "calc(100% - 60px)", sm: 106 },
                      height: { xs: 45, md: 50 },
                      borderRadius: "11px",
                      p: { xs: "10px", md: "14px 26px" },
                      gap: 2,
                      ml: 1,
                    }}
                    onClick={copyViewJobUrl}
                  >
                    <img src={share_icon} />
                    <Typography
                      sx={{
                        color: colors.primary,
                        textTransform: "none",
                        fontFamily: "Poppins",
                        fontSize: { xs: "0.875rem", md: "1rem" },
                      }}
                    >
                      {t("dynamicFormSearchBar.share")}
                    </Typography>
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Advanced filters */}
      {showAdvancedFilters && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Last Name field */}
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{ position: "relative" }}
              ref={(el) => (inputRefs.current.last_name = el)}
            >
              <StyledTypography>
                {t("dynamicFormSearchBar.lastName")}
              </StyledTypography>
              <TextField
                fullWidth
                value={searchParams.last_name || ""}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                onFocus={() => setCurrentAutocompleteField("last_name")}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: 50,
                    backgroundColor: "#fff",
                    "& fieldset": {
                      borderColor: "#D9D9D9",
                    },
                    "&:hover fieldset": {
                      borderColor: "#B0B0B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
              />
              {renderAutocomplete("last_name")}
            </Box>
          </Grid>

          {/* Experience Years field */}
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{ position: "relative" }}
              ref={(el) => (inputRefs.current.experience_years = el)}
            >
              <StyledTypography>
                {t("dynamicFormSearchBar.experienceYears")}
              </StyledTypography>
              <TextField
                fullWidth
                value={searchParams.experience_years || ""}
                onChange={(e) =>
                  handleInputChange("experience_years", e.target.value)
                }
                onFocus={() => setCurrentAutocompleteField("experience_years")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: 50,
                    backgroundColor: "#fff",
                    "& fieldset": {
                      borderColor: "#D9D9D9",
                    },
                    "&:hover fieldset": {
                      borderColor: "#B0B0B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
                variant="outlined"
                type="number"
              />
              {renderAutocomplete("experience_years")}
            </Box>
          </Grid>

          {/* City field - Second row */}
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{ position: "relative" }}
              ref={(el) => (inputRefs.current.city = el)}
            >
              <StyledTypography>{t("dynamicFormSearchBar.localisation", "Localisation")}</StyledTypography>
              <TextField
                fullWidth
                value={searchParams.city || ""}
                onChange={(e) => handleInputChange("city", e.target.value)}
                onFocus={() => setCurrentAutocompleteField("city")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: 50,
                    backgroundColor: "#fff",
                    "& fieldset": {
                      borderColor: "#D9D9D9",
                    },
                    "&:hover fieldset": {
                      borderColor: "#B0B0B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
                variant="outlined"
                InputProps={{
                  endAdornment: searchParams.city && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleInputChange("city", "");
                          setShowAutocomplete(false);
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {renderAutocomplete("city")}
            </Box>
          </Grid>

          {/* Category field */}
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{ position: "relative" }}
              ref={(el) => (inputRefs.current.category_profile = el)}
            >
              <StyledTypography>{t("dynamicFormSearchBar.category", "Catégorie")}</StyledTypography>
              <TextField
                fullWidth
                value={searchParams.category_profile || ""}
                onChange={(e) =>
                  handleInputChange("category_profile", e.target.value)
                }
                onFocus={() => setCurrentAutocompleteField("category_profile")}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: 50,
                    backgroundColor: "#fff",
                    "& fieldset": {
                      borderColor: "#D9D9D9",
                    },
                    "&:hover fieldset": {
                      borderColor: "#B0B0B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
                variant="outlined"
                InputProps={{
                  endAdornment: searchParams.category_profile && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleInputChange("category_profile", "");
                          setShowAutocomplete(false);
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {renderAutocomplete("category_profile")}
            </Box>
          </Grid>

          {/* Subcategory field */}
          <Grid item xs={12} sm={6} md={4}>
            <Box
              sx={{ position: "relative" }}
              ref={(el) => (inputRefs.current.subcategory_profile = el)}
            >
              <StyledTypography>{t("dynamicFormSearchBar.subcategory", "Sous-catégorie")}</StyledTypography>
              <TextField
                fullWidth
                value={searchParams.subcategory_profile || ""}
                onChange={(e) =>
                  handleInputChange("subcategory_profile", e.target.value)
                }
                onFocus={() =>
                  setCurrentAutocompleteField("subcategory_profile")
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: 50,
                    backgroundColor: "#fff",
                    "& fieldset": {
                      borderColor: "#D9D9D9",
                    },
                    "&:hover fieldset": {
                      borderColor: "#B0B0B0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  },
                }}
                variant="outlined"
                InputProps={{
                  endAdornment: searchParams.subcategory_profile && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleInputChange("subcategory_profile", "");
                          setShowAutocomplete(false);
                        }}
                      >
                        <X size={16} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {renderAutocomplete("subcategory_profile")}
            </Box>
          </Grid>

          {/* Education Level field */}
          {renderEducationLevelField()}
        </Grid>
      )}

      {/* Active filters */}
      {activeFilters.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {activeFilters.map((filter, index) => {
            // Find the display label for this field
            const fieldObj = fields.find((f) => f.name === filter.field);
            const displayName = fieldObj ? fieldObj.label : filter.field;

            return (
              <Chip
                key={index}
                label={`${displayName}: ${filter.value}`}
                onDelete={() => handleRemoveFilter(filter)}
                size="small"
                color="primary"
                variant="outlined"
                sx={{
                  maxWidth: "300px",
                  "& .MuiChip-label": {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "0.875rem",
                    lineHeight: "1.4",
                    fontWeight: "400",
                  },
                }}
              />
            );
          })}
          <Chip
            label={t("dynamicFormSearchBar.clearAll")}
            onClick={handleClearAllFilters}
            size="small"
            color="default"
            variant="outlined"
            sx={{
              "& .MuiChip-label": {
                fontSize: "0.875rem",
                lineHeight: "1.4",
                fontWeight: "400",
              },
            }}
          />
        </Box>
      )}

      {/* Source Filter Menu */}
      <Menu
        anchorEl={sourceFilterAnchor}
        open={Boolean(sourceFilterAnchor)}
        onClose={handleSourceFilterMenuClose}
        PaperProps={{
          sx: {
            minWidth: 320,
            maxWidth: 400,
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0, 23, 231, 0.12)",
            border: "1px solid rgba(0, 23, 231, 0.1)",
            mt: 1,
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              color: "#0017E7",
              mb: 2,
              fontFamily: "Poppins",
            }}
          >
            {t("applicationSearchBar.filterBySource", "Filter by Source")}
          </Typography>
          <Divider sx={{ mb: 2, borderColor: "rgba(0, 23, 231, 0.1)" }} />
          
          {/* Source Filters Section or Navigation Checkbox */}
          {onNavigateToApplicationTable ? (
            // Show navigation checkbox for dynamic form component
            <FormControlLabel
              control={
                <Checkbox
                  checked={false}
                  onChange={() => {
                    onNavigateToApplicationTable();
                    handleSourceFilterMenuClose();
                  }}
                  sx={{
                    color: "#0017E7",
                    "&.Mui-checked": {
                      color: "#0017E7",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontFamily: "Poppins",
                    color: "#333",
                    fontWeight: 500,
                  }}
                >
                  {t("dynamicFormSearchBar.backToApplications", "Back to Applications")}
                </Typography>
              }
              sx={{
                width: "100%",
                margin: 0,
                mb: 1,
                "& .MuiFormControlLabel-label": {
                  width: "100%",
                },
              }}
            />
          ) : (
            // Show source filters for other components
            Object.entries(sourceFilters).map(([sourceType, isChecked]) => (
              <FormControlLabel
                key={sourceType}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={() => handleSourceFilterChange(sourceType)}
                    sx={{
                      color: "#0017E7",
                      "&.Mui-checked": {
                        color: "#0017E7",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      fontFamily: "Poppins",
                      color: "#333",
                      fontWeight: 500,
                    }}
                  >
                    {getSourceDisplayName(sourceType)}
                  </Typography>
                }
                sx={{
                  width: "100%",
                  margin: 0,
                  mb: 1,
                  "& .MuiFormControlLabel-label": {
                    width: "100%",
                  },
                }}
              />
            ))
          )}
          
          {/* Small divider between sections */}
          <Divider sx={{ mt: 1.5, mb: 1.5, borderColor: "rgba(0, 23, 231, 0.1)" }} />
          
          {/* Additional Navigation Filters Section */}
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "#0017E7",
              mb: 1,
              fontFamily: "Poppins",
            }}
          >
            {t("applicationSearchBar.additionalFilters", "Additional Filters")}
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={additionalFilters.blacklist}
                onChange={() => handleAdditionalFilterChange("blacklist")}
                sx={{
                  color: "#0017E7",
                  "&.Mui-checked": {
                    color: "#0017E7",
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 20,
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: "0.9rem",
                  fontFamily: "Poppins",
                  color: "#333",
                  fontWeight: 500,
                }}
              >
                {t("applicationSearchBar.blacklist", "Blacklist")}
              </Typography>
            }
            sx={{
              width: "100%",
              margin: 0,
              mb: 1,
              "& .MuiFormControlLabel-label": {
                width: "100%",
              },
            }}
          />
          
          {/* Hide Regular Applications navigation when we're already in DynamicForm component */}
          {!onNavigateToApplicationTable && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={additionalFilters.applicationTable}
                  onChange={() => handleAdditionalFilterChange("applicationTable")}
                  sx={{
                    color: "#0017E7",
                    "&.Mui-checked": {
                      color: "#0017E7",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: 20,
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    fontFamily: "Poppins",
                    color: "#333",
                    fontWeight: 500,
                  }}
                >
                  {t("applicationSearchBar.regularApplications", "Regular Applications")}
                </Typography>
              }
              sx={{
                width: "100%",
                margin: 0,
                mb: 1,
                "& .MuiFormControlLabel-label": {
                  width: "100%",
                },
              }}
            />
          )}
          
          <Divider sx={{ mt: 2, mb: 2, borderColor: "rgba(0, 23, 231, 0.1)" }} />
          
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={handleSourceFilterMenuClose}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                color: "#666",
                fontFamily: "Poppins",
              }}
            >
              {t("applicationSearchBar.cancel", "Cancel")}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                handleSourceFilterMenuClose();
                handleSearch();
              }}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                backgroundColor: "#0017E7",
                fontFamily: "Poppins",
                "&:hover": {
                  backgroundColor: "rgba(0, 23, 231, 0.9)",
                },
              }}
            >
              {t("applicationSearchBar.apply", "Apply")}
            </Button>
          </Box>
        </Box>
      </Menu>

      {/* QR Code Dialog */}
      {showShareButton && (
        <QRCodeDialog
          open={showQRCodeDialog}
          onClose={handleCloseQRCodeDialog}
          qrCodeUrl={qrCodeUrl}
          formData={generateLinkedInPost(adminCompany)}
          callerComponent="DynamicFormSearchBar"
        />
      )}
    </Box>
  );
};

export default DynamicFormSearchBar;